import type { SparkIntent } from '@/core/data/spark/spark.interface';
import { gambits } from '@/core/gambits';
import { createLogger } from '@/core/helpers/logger.helper';
import { firestore } from '@/core/network/firebase';
import type { Result } from '@/core/network/http/httpClient.interface';
import { doc, onSnapshot, Timestamp, type Unsubscribe } from 'firebase/firestore';
import { defineStore } from 'pinia';
import { ref } from 'vue';

const logger = createLogger('SparkIntentStore');

interface SparkIntentFirestore {
  id: string;
  updatedAt: Timestamp;
  currentSubscribers?: number;
}

type SparkIntentWithFullFields = SparkIntent & {
  coordinates?: unknown;
  mapZoom?: number;
};

export const useSparkIntentStore = defineStore('sparkIntent', () => {
  const intentId = ref<string | null>(null);
  const sparkIntent = ref<SparkIntent | null>(null);
  const isIntentLoading = ref<boolean>(false);
  const isIntentError = ref<boolean>(false);
  const isIntentNotFound = ref<boolean>(false);
  const unsubscribeFromIntent = ref<Unsubscribe | null>(null);

  const mergeIntentForSameId = (currentIntent: SparkIntent, nextIntent: SparkIntent): SparkIntent => {
    const currentIntentWithExtras = currentIntent as SparkIntentWithFullFields;
    const nextIntentWithExtras = nextIntent as SparkIntentWithFullFields;

    const mergedIntent: SparkIntentWithFullFields = {
      ...currentIntentWithExtras,
      ...nextIntentWithExtras,
    };

    if (nextIntentWithExtras.availabilities === undefined) {
      mergedIntent.availabilities = currentIntentWithExtras.availabilities;
    }

    if (nextIntentWithExtras.confirmations === undefined) {
      mergedIntent.confirmations = currentIntentWithExtras.confirmations;
    }

    if (nextIntentWithExtras.uuid === undefined) {
      mergedIntent.uuid = currentIntentWithExtras.uuid;
    }

    if (nextIntentWithExtras.createdAt === undefined) {
      mergedIntent.createdAt = currentIntentWithExtras.createdAt;
    }

    if (nextIntentWithExtras.day === undefined) {
      mergedIntent.day = currentIntentWithExtras.day;
    }

    if (nextIntentWithExtras.coordinates === undefined) {
      mergedIntent.coordinates = currentIntentWithExtras.coordinates;
    }

    if (nextIntentWithExtras.mapZoom === undefined) {
      mergedIntent.mapZoom = currentIntentWithExtras.mapZoom;
    }

    return mergedIntent;
  };

  const clearIntentStore = () => {
    if (unsubscribeFromIntent.value !== null) {
      unsubscribeFromIntent.value();
      unsubscribeFromIntent.value = null;
    }

    isIntentLoading.value = false;
    isIntentError.value = false;
    isIntentNotFound.value = false;
    sparkIntent.value = null;
  };

  const subscribeToIntent = (id: string) => {
    if (unsubscribeFromIntent.value !== null) {
      unsubscribeFromIntent.value();
      unsubscribeFromIntent.value = null;
    }

    let didExist = false;
    let shouldSkipBootstrapRefresh = sparkIntent.value?.id === id;

    const docRef = doc(firestore, 'intents', id);

    unsubscribeFromIntent.value = onSnapshot(
      docRef,
      (docSnap) => {
        if (docSnap.exists()) {
          logger.info(`Intent ${id} Firestore doc found.`);
          didExist = true;
          const data = docSnap.data() as SparkIntentFirestore;

          if (shouldSkipBootstrapRefresh) {
            shouldSkipBootstrapRefresh = false;
            return;
          }

          if (!sparkIntent.value?.updatedAt || data.updatedAt.toDate() > new Date(sparkIntent.value.updatedAt)) {
            fetchIntent(docSnap.id);
          }
        } else if (didExist) {
          logger.info(`Intent ${id} Firestore doc deleted.`);
          isIntentNotFound.value = true;
          sparkIntent.value = null;
        } else {
          logger.warn(`Intent ${id} Firestore doc not found. Creating.`);
          gambits.sparkService.createFirestoreIntentDoc(id);
        }
      },
      (error) => {
        logger.error(`Intent ${id} Firestore snapshot error:`, error);
        isIntentLoading.value = false;
      },
    );
  };

  const fetchIntent = async (id: string): Promise<Result<SparkIntent> | undefined> => {
    intentId.value = id;
    isIntentLoading.value = true;
    isIntentError.value = false;
    isIntentNotFound.value = false;

    const isRefresh = sparkIntent.value?.id === id;

    if (!isRefresh) {
      sparkIntent.value = null;
    }

    const result = await gambits.sparkService.getIntent(id);
    isIntentLoading.value = false;

    if (result?.data) {
      sparkIntent.value = result.data;

      if (!isRefresh) {
        subscribeToIntent(result.data.id);
      }
    } else if (result?.error?.code === 404) {
      isIntentNotFound.value = true;
      sparkIntent.value = null;
    } else {
      isIntentError.value = true;
    }

    return result;
  };

  async function replaceIntent(newIntent: SparkIntent) {
    intentId.value = newIntent.id;
    isIntentLoading.value = false;
    isIntentError.value = false;
    isIntentNotFound.value = false;

    const isRefresh = sparkIntent.value?.id === newIntent.id;

    sparkIntent.value = isRefresh && sparkIntent.value ? mergeIntentForSameId(sparkIntent.value, newIntent) : newIntent;

    if (!isRefresh) {
      subscribeToIntent(newIntent.id);
    }
  }

  return {
    clearIntentStore,
    fetchIntent,
    replaceIntent,
    unsubscribeFromIntent,

    sparkIntent,
    isIntentNotFound,
    isIntentError,
    intentId,
  };
});
